import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 1 | 2 | 3 | 4;
type Step = number;
export function PaidReportInputs() {
  const navigate = useNavigate();
  const [showEntry, setShowEntry] = useState(true);
  const [currentPhase, setCurrentPhase] = useState<Phase>(1);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [formData, setFormData] = useState({
    // Phase 1: Entry Pressure
    entryReason: '',
    // Phase 2: Core Loop
    trigger: '',
    dominantResponse: [] as string[],
    typicalOutcome: '',
    // Phase 3: Generalization
    whereElse: '',
    patternDuration: '',
    // Phase 4: Current Strain
    innerTension: '',
    hardest: ''
  });
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleMultiSelect = (field: string, value: string) => {
    setFormData(prev => {
      const current = prev[field as keyof typeof prev] as string[];
      if (current.includes(value)) {
        return {
          ...prev,
          [field]: current.filter(v => v !== value)
        };
      } else if (current.length < 2) {
        return {
          ...prev,
          [field]: [...current, value]
        };
      }
      return prev;
    });
  };
  const handleStartDiagnostic = () => {
    setShowEntry(false);
  };
  const handleNext = () => {
    // Phase 1 navigation (Entry Pressure - 1 question)
    if (currentPhase === 1) {
      if (currentStep === 1 && !formData.entryReason) return;
      setCurrentPhase(2);
      setCurrentStep(1);
    }
    // Phase 2 navigation (Core Loop - 3 questions)
    else if (currentPhase === 2) {
      if (currentStep === 1 && !formData.trigger) return;
      if (currentStep === 2 && formData.dominantResponse.length === 0) return;
      if (currentStep === 3 && !formData.typicalOutcome) return;
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase(3);
        setCurrentStep(1);
      }
    }
    // Phase 3 navigation (Generalization - 2 questions)
    else if (currentPhase === 3) {
      if (currentStep === 1 && !formData.whereElse) return;
      if (currentStep === 2 && !formData.patternDuration) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase(4);
        setCurrentStep(1);
      }
    }
    // Phase 4 navigation (Current Strain - 2 questions)
    else if (currentPhase === 4) {
      if (currentStep === 1 && !formData.innerTension) return;
      if (currentStep === 2 && !formData.hardest) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowEndScreen(true);
      }
    }
  };
  const handleGenerateReport = () => {
    setIsGenerating(true);
    console.log('Paid report inputs:', formData);
    // Simulate report generation
    setTimeout(() => {
      navigate('/paid-report-output');
    }, 3000);
  };
  // Phase 1 options - Entry Pressure
  const entryReasonOptions = ['A pattern keeps repeating', 'I feel blocked or stuck', "I don't trust my decisions", 'I feel conflicted internally', 'I want clarity before acting'];
  // Phase 2 options - Core Loop
  const triggerOptions = ['Specific person', 'Type of situation', 'Time pressure', 'Uncertainty', 'Responsibility'];
  const responseOptions = ['Freeze or delay', 'Over-analyze', 'Over-perform', 'Withdraw', 'Push harder', 'Justify or explain'];
  const outcomeOptions = ['Temporary relief', 'Increased tension', 'Pattern repeats', 'Exhaustion', 'Avoidance'];
  // Phase 3 options - Generalization
  const whereElseOptions = ['Work', 'Relationships', 'Family', 'Personal decisions', 'Multiple contexts'];
  const durationOptions = ['Recent (weeks)', 'Ongoing (months)', 'Long-term (years)', 'Recurring pattern'];
  // Phase 4 options - Current Strain
  const tensionOptions = ['Guilt', 'Resentment', 'Fear', 'Inadequacy', 'Frustration'];
  const hardestOptions = ['Admitting the pattern', 'Changing behavior', 'Letting go of control', 'Trusting the process', 'Facing consequences'];
  if (showEntry) {
    return <>
        <Helmet data-id="element-2658">
          <title data-id="element-2659">Diagnostic Input — Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-2660" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-2661">
          <header data-id="element-2662">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2663">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2664">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2665">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2666" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-2667">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-2668">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-2669">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-2670">
                Diagnostic input
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed max-w-xl mx-auto" data-id="element-2671">
                Answering these helps generate an accurate report.
                <br data-id="element-2672" />
                There are no right or wrong responses.
              </p>
              <button onClick={handleStartDiagnostic} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2673">
                Start Diagnostic
              </button>
            </div>
          </main>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-2674">
          <title data-id="element-2675">Generating Report — Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-2676" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-2677">
          <div className="text-center" data-id="element-2678">
            <p className="text-xl text-[#F5F5F5]/70" data-id="element-2679">Analyzing patterns...</p>
          </div>
        </div>
      </>;
  }
  if (showEndScreen) {
    return <>
        <Helmet data-id="element-2680">
          <title data-id="element-2681">Ready to Generate — Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-2682" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-2683">
          <header data-id="element-2684">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2685">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2686">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2687">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2688" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-2689">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-2690">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-2691">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-2692">
                Ready to generate your report
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed" data-id="element-2693">
                Context, response patterns, and constraints have been captured.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2694">
                <button onClick={handleGenerateReport} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2695">
                  Generate Full Report
                </button>
                <button onClick={() => {
                setShowEndScreen(false);
                setCurrentPhase(1);
                setCurrentStep(1);
              }} className="px-8 py-5 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2696">
                  Edit responses
                </button>
              </div>
            </div>
          </main>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-2697">
        <title data-id="element-2698">Diagnostic Input — Decode | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-2699" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-2700">
        <header data-id="element-2701">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2702">
            <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-2703">
              <p className="text-xs text-[#F5F5F5]/50 text-center tracking-wider" data-id="element-2704">
                Diagnostic progress — Phase {currentPhase} of 4
              </p>
            </div>
          </nav>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-2705">
          <div className="max-w-2xl mx-auto w-full" data-id="element-2706">
            {/* Phase 1: Entry Pressure */}
            {currentPhase === 1 && currentStep === 1 && <div data-id="element-2707">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2708">
                  Why are you doing this decode now?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2709">
                  {entryReasonOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.entryReason === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2710">
                      <input type="radio" name="entryReason" value={option} checked={formData.entryReason === option} onChange={e => handleOptionSelect('entryReason', e.target.value)} className="sr-only" data-id="element-2711" />
                      <span className="text-base" data-id="element-2712">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.entryReason} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2713">
                  Continue
                </button>
              </div>}

            {/* Phase 2: Core Loop */}
            {currentPhase === 2 && currentStep === 1 && <div data-id="element-2714">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2715">
                  What typically triggers this pattern?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2716">
                  {triggerOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.trigger === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2717">
                      <input type="radio" name="trigger" value={option} checked={formData.trigger === option} onChange={e => handleOptionSelect('trigger', e.target.value)} className="sr-only" data-id="element-2718" />
                      <span className="text-base" data-id="element-2719">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.trigger} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2720">
                  Continue
                </button>
              </div>}

            {currentPhase === 2 && currentStep === 2 && <div data-id="element-2721">
                <h1 className="text-3xl md:text-4xl font-medium mb-6 text-center leading-tight tracking-tight" data-id="element-2722">
                  What do you typically do when this happens?
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-2723">
                  Select up to 2 responses
                </p>
                <div className="space-y-4 mb-12" data-id="element-2724">
                  {responseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.dominantResponse.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2725">
                      <input type="checkbox" name="dominantResponse" value={option} checked={formData.dominantResponse.includes(option)} onChange={e => handleMultiSelect('dominantResponse', e.target.value)} className="sr-only" data-id="element-2726" />
                      <span className="text-base" data-id="element-2727">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={formData.dominantResponse.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2728">
                  Continue
                </button>
              </div>}

            {currentPhase === 2 && currentStep === 3 && <div data-id="element-2729">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2730">
                  What typically happens after?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2731">
                  {outcomeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.typicalOutcome === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2732">
                      <input type="radio" name="typicalOutcome" value={option} checked={formData.typicalOutcome === option} onChange={e => handleOptionSelect('typicalOutcome', e.target.value)} className="sr-only" data-id="element-2733" />
                      <span className="text-base" data-id="element-2734">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.typicalOutcome} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2735">
                  Continue
                </button>
              </div>}

            {/* Phase 3: Generalization */}
            {currentPhase === 3 && currentStep === 1 && <div data-id="element-2736">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2737">
                  Where else does this pattern appear?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2738">
                  {whereElseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.whereElse === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2739">
                      <input type="radio" name="whereElse" value={option} checked={formData.whereElse === option} onChange={e => handleOptionSelect('whereElse', e.target.value)} className="sr-only" data-id="element-2740" />
                      <span className="text-base" data-id="element-2741">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.whereElse} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2742">
                  Continue
                </button>
              </div>}

            {currentPhase === 3 && currentStep === 2 && <div data-id="element-2743">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2744">
                  How long has this pattern been active?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2745">
                  {durationOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.patternDuration === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2746">
                      <input type="radio" name="patternDuration" value={option} checked={formData.patternDuration === option} onChange={e => handleOptionSelect('patternDuration', e.target.value)} className="sr-only" data-id="element-2747" />
                      <span className="text-base" data-id="element-2748">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.patternDuration} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2749">
                  Continue
                </button>
              </div>}

            {/* Phase 4: Current Strain */}
            {currentPhase === 4 && currentStep === 1 && <div data-id="element-2750">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2751">
                  What underlying pressure feels most active?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2752">
                  {tensionOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.innerTension === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2753">
                      <input type="radio" name="innerTension" value={option} checked={formData.innerTension === option} onChange={e => handleOptionSelect('innerTension', e.target.value)} className="sr-only" data-id="element-2754" />
                      <span className="text-base" data-id="element-2755">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.innerTension} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2756">
                  Continue
                </button>
              </div>}

            {currentPhase === 4 && currentStep === 2 && <div data-id="element-2757">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2758">
                  What feels hardest about this pattern right now?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2759">
                  {hardestOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.hardest === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2760">
                      <input type="radio" name="hardest" value={option} checked={formData.hardest === option} onChange={e => handleOptionSelect('hardest', e.target.value)} className="sr-only" data-id="element-2761" />
                      <span className="text-base" data-id="element-2762">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.hardest} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2763">
                  Continue
                </button>
              </div>}
          </div>
        </main>
      </div>
    </>;
}